// src/components/JoinMembershipModal.js
// Join a creator's channel (evergreen 3.5a channel memberships; tiers R5) or
// a team (evergreen 3.5b team memberships): ONE component, parameterized by
// kind. A team has one price and no tiers.
//
// The target is the `target` prop {kind: 'channel'|'team', id, name}. Without
// it the channel form {kind: 'channel', id: creatorUid, name: creatorName} is
// derived from the 3.5a props, so a mount that passes only creatorUid and
// creatorName (VideoWatch) behaves exactly as before. The team noun is
// "Team X" (teamDisplayName); the offer's targetName wins over target.name.
//
// Mounted at SCREEN level (StreamWatch beside StreamRumble, PublicProfile,
// VideoWatch, Your memberships, TeamPage, TeamStream), never inside
// StreamChat, so polls, chat collapse and breakpoints never tear down a
// payment form. The parent KEYS it by target (key={'join:' + kind + ':' +
// id}; the 3.5a channel-only mounts key by channel, key={'join:' +
// creatorUid}), so a target change unmounts it with its form. Closing
// destroys the mounted checkout and drops the stage, the offer and the join
// time.
//
// Stages:
//  1. panel, from getMembershipOffer: a viewer.state layout (already a
//     member, set to end, payment needs attention, being set up) always
//     wins; else available:false -> the unavailable stage (no price, box or
//     Continue; at the 24-hour limit in 'limits' mode the L3m / L3bm / L3pm
//     / L4m line with its cooldown); else the join panel: the tiers the
//     creator offers (one tier looks like the one-price panel, with the tier
//     name above the price; several are a radio list, each above the lowest
//     starting "Everything in {lower tier}"), the payer's own price, the
//     announce option, the server's disclosure and consent text FOR THE
//     SELECTED TIER, an UNTICKED box and "Continue to payment", disabled
//     until it is ticked. A team has one price and no tiers: its panel is
//     the one-price panel, and it never lists a videos perk. In 'limits'
//     mode an unverified payer sees L1m and
//     tiers above the room are disabled with L2m; for 24 h after a join
//     refused for its card, L10c / L10d sit above the box.
//     R6 (3.13.3): for an unverified payer the server flags (emailGate),
//     the J7 email block sits in place of the consent box and "Continue to
//     payment" (the tiers and prices stay above it): "Send confirmation
//     email", then J8 with "I've confirmed it" / "Send it again"; the panel
//     re-checks on window focus and every 5 seconds for 2 minutes after a
//     send (auth reload, then the offer again). A waitGate shows J10 there
//     instead. J1 sits at the panel's foot for every payer.
//  2. pay (R6, 3.13.2; the server answered flow 'element'): J0 as the title,
//     the server's disclosure, Stripe's Payment Element (card only, Link
//     hidden), and "Join for {price} a month" (J3). The tap makes a
//     ConfirmationToken (nothing is charged) and confirmMembershipJoin
//     checks the card before any charge and confirms on the server. A
//     refusal (J4, J5, J5L) or a decline (J5d, J5a) keeps this stage open
//     for another card. When the bank wants to confirm the payment
//     (continue_checkout; founder decision 10-18), nothing was charged:
//     the approved line shows at once and the SAME join (tier, announce
//     choice, consent) continues on Embedded Checkout below it, where
//     Stripe runs the bank's check. This page never runs it and never gets
//     the payment's client secret; later Continues in this opening (and,
//     R6 review R6C-BC-3, a reopen for the same channel within 35 minutes
//     on this page load) go straight to Embedded Checkout.
//     checkout: the server answered flow 'checkout' (its emergency switch,
//     the attempt limit, a Stripe credit balance, or the continuation
//     above): the disclosure directly above Stripe's embedded form, as
//     before R6.
//  3. welcome: entered ONLY from the embedded form's own completion callback
//     or a successful confirmMembershipJoin in this session. It waits on the
//     target's pair doc (useMyMembership: the channel pair or the TEAM
//     pair) and never on Stripe: the webhook
//     records the payment. A join refused for its card (the pair doc's
//     startRefused) shows L10a / L10b here.
//  4. switch (R5, 2.6.2; channels only): a paid, renewing member picks the
//     tier they want
//     from their next renewal (the server's switchOptions, each with its own
//     disclosure and consent text), or keeps their current tier while a
//     switch is pending ("Keep {From}"). Nothing is charged when switching.
//     For a payer the limit applies, a switch up shows F4c (the difference it
//     counts) and a tier above the room is disabled with L2s.
//
// openTo ('switch' | 'stay', optional): open straight at the switch stage
// (Your memberships, a tier-locked video or chat); 'stay' preselects
// "Keep {From}" (and lands on the member view when no stay is left).
// minTierKey (optional): the tier the opening gate needs (a tier-limited
// video or members-only chat); gatePerk (optional, 'membersVideos' |
// 'membersChat'): the perk that gate needs. The join panel and the switch
// stage preselect the first tier at or above minTierKey that carries
// gatePerk, never one that would not unlock what the member tapped; when no
// tier does (or a pending switch already does), nothing is preselected and
// the fan picks. onChanged runs after a switch succeeds (the parent reloads).
// For a team (no tiers), openTo, minTierKey and gatePerk are forced to null.
//
// A team join (3.5b, STEP3_5B_MERGE_DELTA.md section 5 row 20) runs this same
// flow: the card form first, Embedded Checkout when the bank asks, never a
// client secret in the browser, and the same email gate and wait. Only the
// lines that name the target differ: J0 (W1), J10's lead (W2), L10a / L10b
// (W3 / W4), and the confirmation email returns to Verification Center
// (returnTo 'verification': there is no team return route). The bank-ask
// memory is keyed 'team:' + teamId.
//
// Every control that leaves the modal closes it first (goFromModal); "Done"
// and "Not now" only close. Fan copy is money-free; the price shown is the
// payer's own. "Join", never "Subscribe".

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Pressable,
  Modal,
  ScrollView,
  ActivityIndicator,
  Switch,
  StyleSheet,
  Platform,
  Linking,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../firebaseConfig';
import ConsentCheckbox, { ConsentHint } from './ConsentCheckbox';
import SpendingLimitNotice from './SpendingLimitNotice';
import { openTermsOfService } from '../utils/openTermsOfService';
import { CURRENT_TOS_VERSION, parseTosVersion, compareTosVersions } from '../constants/tosUpdate';
import {
  getMembershipOffer,
  createMembershipCheckout,
  confirmMembershipJoin,
  sendEmailConfirmation,
  switchMyMembershipTier,
  listMyMemberships,
  mountEmbeddedCheckout,
  mountMembershipElement,
  membershipErrorKind,
  membershipErrorMessage,
  logMembershipError,
  useMyMembership,
  deliveredPerks,
  formatMonthlyPrice,
  formatMembershipDate,
  teamDisplayName,
  LISTED_PERKS_BY_KIND,
  PERK_LABELS_BY_KIND,
} from '../utils/membershipPayment';
import {
  isTierKey,
  tierNameOf,
  tierRankOf,
  tierPerkLines,
  joinLead,
  VIDEOS_FOOTER,
  SWITCH_TITLE,
  switchLead,
  CURRENT_TIER_CHIP,
  switchButtonLabel,
  keepTierLabel,
  switchDoneText,
  stayDoneText,
  tierPriceLine,
  pendingSwitchLine,
  SWITCH_TIER_LABEL,
  longDate,
  shortDate,
} from '../utils/membershipTiers';
import {
  LINES,
  VERIFY_ROUTE,
  USE_ANOTHER_CARD_LABEL,
  formatCurrency,
  formatCooldown,
  limitLines,
  joinRefusedLine,
  joinRefundLine,
  switchLimitLine,
  switchCountLine,
  withVerify,
  verifyLine,
} from '../utils/spendingLimit';
import {
  payStageTitle,
  teamPayStageTitle,
  teamRefundLine,
  STATEMENT_LINE,
  BANK_CONTINUE_LINE,
  payButtonLabel,
  TRY_AGAIN_LABEL,
  emailGateLead,
  SEND_CONFIRMATION_LABEL,
  emailSentLine,
  CONFIRMED_IT_LABEL,
  SEND_AGAIN_LABEL,
  notConfirmedYetLine,
  sendLimitLine,
  WRONG_ADDRESS_LINE,
  NO_ADDRESS_LINE,
  EMAIL_POLL_EVERY_MS,
  EMAIL_POLL_FOR_MS,
  waitPanelLine,
  joinAtText,
  maskEmail,
  joinResultAction,
  cardRefusalLine,
  joinGatesOf,
  rememberBankAsk,
  bankAskedRecently,
} from '../utils/membershipJoin';

const IS_WEB = Platform.OS === 'web';

const COLORS = {
  CARD: '#111',
  CARD_BORDER: '#222',
  GOLD: '#FCD000',
  TEXT: '#EDEDED',
  DIM: '#888',
  RED: '#e05555',
};

// After membership-busy the payer may retry after a few seconds.
const BUSY_RETRY_MS = 4000;
// The welcome shows "Finishing up" once the pair doc has not moved for this long.
const FINISHING_UP_AFTER_MS = 20000;
// A card refusal the pair doc carries belongs to THIS join when it is no
// older than the join's start (client and server clocks may differ a little).
const REFUSAL_SKEW_MS = 30000;
// setTimeout's ceiling; a longer delay fires at once.
const MAX_TIMER_MS = 2 ** 31 - 1;
// A Stripe.js error that is not about the card itself: the house fallback.
const GENERIC_FAILURE = 'Something went wrong. Please try again.';

// The account's Auth emailVerified after a reload: true / false, or null
// when it cannot be read (the server then decides through the offer).
async function reloadEmailVerified() {
  const u = auth.currentUser;
  if (!u) return null;
  try {
    await u.reload();
    return auth.currentUser?.emailVerified === true;
  } catch (_) {
    return null;
  }
}

const EXISTING_STATES = ['active', 'canceling', 'past_due', 'activating'];

const TEXT = {
  activating: 'Your membership is being set up. It will show here in a moment.',
  pastDue: 'Your membership payment needs attention.',
  lastAttemptFailed: "Your last payment didn't go through. You can try again.",
  termsRefresh: 'Please refresh the page to see the updated Terms.',
  termsReview: 'Please review the updated Terms to join.',
  checkLatest: 'Check Your memberships for the latest on this membership.',
  finishingUp: 'Finishing up. Your membership will show here in a moment.',
  formFailed: 'Could not open the payment form. Please try again.',
  closedFallback: "This channel isn't taking memberships right now.",
  webOnly: 'Memberships are available on the web app.',
  signUp: 'Sign up or log in to join.',
};

// The copy that names the target, by kind ({name} is the rendered noun:
// the creator's name for a channel, "Team X" for a team). The channel column
// is the 3.5a literal text; everything else in TEXT is kind-neutral.
const TEXT_BY_KIND = {
  channel: {
    title: (name) => `Join ${name}'s channel`,
    memberTitle: (name) => `Member of ${name}'s channel`,
    support: (name) => `Support ${name} every month and get:`,
    announce: (name) => `If ${name} is live when your membership starts, announce me in chat`,
    existing: (name) => `You're a member of ${name}'s channel.`,
    welcome: (name) => `You're a member of ${name}'s channel. Your perks are on.`,
    welcomeRemoved: (name) => `You're a member of ${name}'s channel.`,
    closedFallback: TEXT.closedFallback,
  },
  team: {
    title: (name) => `Join ${name}`,
    memberTitle: (name) => `Member of ${name}`,
    support: (name) => `Support ${name} every month and get:`,
    announce: (name) => `If ${name} is streaming as a squad when your membership starts, announce me in squad chat`,
    existing: (name) => `You're a member of ${name}.`,
    welcome: (name) => `You're a member of ${name}. Your perks are on.`,
    welcomeRemoved: (name) => `You're a member of ${name}.`,
    closedFallback: "This team isn't taking memberships right now.",
  },
};

const isStr = (v) => typeof v === 'string' && v.length > 0;

// True when this bundle's Terms are older than the version the server
// requires: its Terms screen would accept the old version, so a refresh comes
// first.
function bundleTermsBehind(required) {
  const need = parseTosVersion(required);
  const have = parseTosVersion(CURRENT_TOS_VERSION);
  return !!need && !!have && compareTosVersions(have, need) < 0;
}

// The one Terms branch, for the unavailable stage (reasonToken
// terms-required) and a terms-required refusal at checkout.
function termsError(offer) {
  return bundleTermsBehind(offer?.termsVersionRequired)
    ? { text: TEXT.termsRefresh, action: 'refresh' }
    : { text: TEXT.termsReview, action: 'review_terms' };
}

/**
 * W3 / W4 (3.5b): a team join refused for its card, the shape of
 * spendingLimit.joinRefundLine ({id, text, verify, button}) with the team's
 * lines (membershipJoin.teamRefundLine). L10a goes through withVerify (its
 * verify clause follows the account's verify state); L10b has no button.
 */
export function teamJoinRefundLine({ reason, teamName, priceCents, creditCents, verify } = {}) {
  const raw = teamRefundLine({
    reason,
    teamName,
    last4: null,
    price: formatCurrency(priceCents),
    credit: Number(creditCents) > 0 ? formatCurrency(creditCents) : '',
  });
  if (!raw) return null;
  return raw.verifyClause
    ? { id: raw.id, ...withVerify(raw.text, verify) }
    : { id: raw.id, text: raw.text, verify: verifyLine(verify).kind, button: null };
}

/**
 * What the join panel can sell: the server's tiers (sellable, by rank), each
 * with its own price, perks, disclosure and consent text. An older server
 * sends one price and no tiers: one choice with tierKey null (the panel then
 * looks exactly as before tiers).
 */
export function joinChoicesOf(o) {
  const priced = (t) => !!t && Number.isInteger(t.priceCents) && t.priceCents > 0
    && isStr(t.disclosure) && isStr(t.consentText);
  if (Array.isArray(o?.tiers) && o.tiers.length) {
    return o.tiers
      .filter((t) => priced(t) && isTierKey(t.tierKey))
      .map((t) => ({ ...t, perks: t.perks || {} }));
  }
  if (priced(o)) {
    return [{
      tierKey: null, priceCents: o.priceCents, disclosure: o.disclosure, consentText: o.consentText, perks: o.perks || {},
    }];
  }
  return [];
}

// The member's switch options (2.7.1), valid rows only.
export function switchOptionsOf(o) {
  const list = Array.isArray(o?.viewer?.switchOptions) ? o.viewer.switchOptions : [];
  return list.filter((s) => s && isTierKey(s.tierKey) && (s.kind === 'switch' || s.kind === 'stay')
    && Number.isInteger(s.priceCents) && s.priceCents > 0 && isStr(s.disclosure) && isStr(s.consentText));
}

// The perks an opening gate can need (ADV2-1).
const GATE_PERKS = ['membersVideos', 'membersChat'];

// The opening gate: {minRank, perk}, or null when there is none (no minimum
// above Spark and no perk).
function gateOf(minTierKey, gatePerk) {
  const minRank = tierRankOf(minTierKey);
  const perk = GATE_PERKS.includes(gatePerk) ? gatePerk : null;
  return minRank > 1 || perk ? { minRank: Math.max(1, minRank), perk } : null;
}

// Does a tier unlock the gate? perks: that tier's perks (unknown = none).
function unlocksGate(gate, tierKey, perks) {
  return tierRankOf(tierKey) >= gate.minRank && (!gate.perk || perks?.[gate.perk] === true);
}

// The first of `list` that unlocks the gate, inside the room when one is.
// undefined: no gate (the caller's own default applies); null: a gate that
// nothing in `list` unlocks (nothing is preselected). perksOf(c): c's perks.
function atOrAbove(list, gate, perksOf) {
  if (!gate) return undefined;
  const up = list.filter((c) => unlocksGate(gate, c.tierKey, perksOf(c)));
  return up.find((c) => c.withinLimit !== false) || up[0] || null;
}

// A tier's perks from the offer's sellable tiers (a switch option carries none).
function offerTierPerks(o, tierKey) {
  const t = Array.isArray(o?.tiers) ? o.tiers.find((x) => x && x.tierKey === tierKey) : null;
  return t?.perks || null;
}

// True when the join panel has tiers and none of them unlocks the opening
// gate: the panel then lists them with nothing picked (ADV2-2).
export function joinGateUnreached(choices, minTierKey = null, gatePerk = null) {
  const tiered = choices.length > 0 && choices[0].tierKey !== null;
  return tiered && atOrAbove(choices, gateOf(minTierKey, gatePerk), (c) => c.perks) === null;
}

// The tier picked by default: with a gate (what opened the modal), the first
// tier that unlocks it, or none (null) when no tier does; else the first one
// inside the 24-hour room (all of them when no limit applies), else the
// first. An older server's one-price panel (no tiers) has no gate.
export function defaultChoice(choices, minTierKey = null, gatePerk = null) {
  const tiered = choices.length > 0 && choices[0].tierKey !== null;
  const up = tiered ? atOrAbove(choices, gateOf(minTierKey, gatePerk), (c) => c.perks) : undefined;
  if (up) return up.tierKey ?? null;
  if (up === null) return null;
  const fit = choices.find((c) => c.withinLimit !== false);
  return (fit || choices[0] || null)?.tierKey ?? null;
}

// The switch stage's default: 'stay' preselects Keep {From} (null when no
// stay is left: the pending switch was settled); otherwise, with a gate, the
// first switch that unlocks it, or none (null) when no switch does or the
// pending switch already does (its "Switches to" line stands, ADV2-3); else
// the first switch inside the room.
export function pickSwitch(o, want, minTierKey = null, gatePerk = null) {
  const opts = switchOptionsOf(o);
  const stay = opts.find((s) => s.kind === 'stay');
  if (want === 'stay') return stay ? stay.tierKey : null;
  const gate = gateOf(minTierKey, gatePerk);
  const next = o?.viewer?.nextTierKey;
  if (gate && isTierKey(next) && unlocksGate(gate, next, offerTierPerks(o, next))) return null;
  const up = atOrAbove(opts.filter((s) => s.kind === 'switch'), gate, (s) => offerTierPerks(o, s.tierKey));
  if (up) return up.tierKey;
  if (up === null) return null;
  const fit = opts.find((s) => s.kind === 'switch' && s.withinLimit !== false);
  return (fit || stay || opts[0] || null)?.tierKey ?? null;
}

export function offerStage(o, openTo) {
  const vs = o?.viewer?.state;
  if (EXISTING_STATES.includes(vs)) {
    const opts = vs === 'active' ? switchOptionsOf(o) : [];
    // 'stay' needs a stay option (a stale "Keep {From}" lands on the member view).
    const fits = openTo === 'stay' ? opts.some((s) => s.kind === 'stay') : opts.length > 0;
    return openTo && fits ? 'switch' : 'state';
  }
  if (o?.available !== true) return 'unavailable';
  return joinChoicesOf(o).length ? 'panel' : 'unavailable';
}

// The target of this modal: the `target` prop, else the 3.5a channel form
// from creatorUid / creatorName. Any kind other than 'team' is a channel.
function targetOfProps(target, creatorUid, creatorName) {
  if (target && typeof target === 'object') {
    return { kind: target.kind === 'team' ? 'team' : 'channel', id: target.id || null, name: target.name };
  }
  return { kind: 'channel', id: creatorUid || null, name: creatorName };
}

export default function JoinMembershipModal({
  target, creatorUid, creatorName, visible, onClose, navigation,
  openTo: openToProp = null, onChanged, minTierKey: minTierKeyProp = null, gatePerk: gatePerkProp = null,
}) {
  const tgt = targetOfProps(target, creatorUid, creatorName);
  const targetKind = tgt.kind;
  const targetId = tgt.id;
  const copy = TEXT_BY_KIND[targetKind];
  // A team has one price and no tiers: no switch stage and no tier gate.
  const openTo = targetKind === 'team' ? null : openToProp;
  const minTierKey = targetKind === 'team' ? null : minTierKeyProp;
  const gatePerk = targetKind === 'team' ? null : gatePerkProp;
  // The bank-ask memory (R6C-BC-3) is per target; a team key never meets a
  // channel uid.
  const bankKey = targetKind === 'team' ? 'team:' + targetId : targetId;

  // Parent callbacks live in refs: a re-render of the screen must never tear
  // down the embedded checkout mid-payment.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const navigationRef = useRef(navigation);
  navigationRef.current = navigation;
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;

  const [uid, setUid] = useState(() => auth.currentUser?.uid || null);
  const [authReady, setAuthReady] = useState(() => !!auth.currentUser);
  useEffect(() => onAuthStateChanged(auth, (u) => {
    setUid(u ? u.uid : null);
    setAuthReady(true);
  }), []);

  // idle | loading | offer_error | state | unavailable | panel | processing |
  // pay | checkout | welcome | switch | switched
  const [stage, setStage] = useState('idle');
  const [offer, setOffer] = useState(null);
  const [selected, setSelected] = useState(null);  // the picked tierKey (null = the one-price panel)
  const [consent, setConsent] = useState(false);
  const [announce, setAnnounce] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);   // { text, action?: 'refresh'|'review_terms'|'memberships' }
  const [notice, setNotice] = useState(null); // a panel line kept across an offer refetch
  const [retryBlocked, setRetryBlocked] = useState(false);
  const [embedded, setEmbedded] = useState(null); // { clientSecret, publishableKey, disclosure }
  const [joinStartedAtMs, setJoinStartedAtMs] = useState(0);
  const [joinedPriceCents, setJoinedPriceCents] = useState(0);
  const [finishingUp, setFinishingUp] = useState(false);
  // Switch stage: the picked option's tierKey, and the result line.
  const [switchSel, setSwitchSel] = useState(null);
  const [switchDone, setSwitchDone] = useState(null); // { text }
  // The pending switch's price (Your memberships has it; the offer does not).
  const [pendingPriceCents, setPendingPriceCents] = useState(null);
  // Join v2 (R6): the element checkout (createMembershipCheckout's flow
  // 'element' answer), whether its form is ready, and the line a refusal or
  // a decline left on the pay stage ({id, text, button?, tryAgain, anotherCard}).
  const [element, setElement] = useState(null);
  const [elementReady, setElementReady] = useState(false);
  const [payLine, setPayLine] = useState(null);
  // The bank wanted to confirm the element payment (founder decision 10-18):
  // the Embedded Checkout stage of that same join shows BANK_CONTINUE_LINE.
  const [bankLine, setBankLine] = useState(false);
  // The email gate (R6): 'ask' (J7) or 'sent' (J8); a line under it (J9 or a
  // refusal); the masked address the send returned; sends refused until
  // (J9r); the auto re-check runs until pollUntil.
  const [emailStep, setEmailStep] = useState('ask');
  const [emailNote, setEmailNote] = useState(null);
  const [sentMasked, setSentMasked] = useState(null);
  // R6 review R6C-4: the server said the account has no sign-in address.
  const [emailMissing, setEmailMissing] = useState(false);
  const [sendBlockedUntil, setSendBlockedUntil] = useState(0);
  const [pollUntil, setPollUntil] = useState(0);
  const [cooldownTick, setCooldownTick] = useState(0);

  const seqRef = useRef(0);      // bumps on every open/close: late replies are dropped
  const busyRef = useRef(false);
  const retryTimerRef = useRef(null);
  const checkoutBoxRef = useRef(null);
  // The pay stage's form: its DOM node and the mounted controller.
  const payBoxRef = useRef(null);
  const payCtlRef = useRef(null);
  // The join as last sent ({args, priceCents, disclosure}): the continuation
  // on Embedded Checkout sends the same tier, announce choice and consent.
  const joinRef = useRef(null);
  // Once the bank wanted to confirm a payment in this opening (or, R6 review
  // R6C-BC-3, for this channel in the last 35 minutes), Continue asks for
  // Embedded Checkout directly (the bank's check runs there).
  const checkoutOnlyRef = useRef(false);
  // Where an offer reload lands for a member: the switch stage when we were in it.
  const openToRef = useRef(openTo);
  // The opening gate's tier (read at every offer load, so a reopen for
  // another video or chat minimum uses its own).
  const minTierRef = useRef(minTierKey);
  minTierRef.current = isTierKey(minTierKey) ? minTierKey : null;
  const gatePerkRef = useRef(gatePerk);
  gatePerkRef.current = GATE_PERKS.includes(gatePerk) ? gatePerk : null;

  // The target's pair doc (channel or team), only while open (the screen
  // keeps its own listener).
  const m = useMyMembership(visible ? targetKind : null, targetId);

  const name = targetKind === 'team'
    ? teamDisplayName(offer?.targetName || tgt.name)
    : (offer?.creatorName || tgt.name || '').trim() || 'this creator';
  const listedPerks = LISTED_PERKS_BY_KIND[targetKind];
  const perkLabels = PERK_LABELS_BY_KIND[targetKind];

  const requestClose = useCallback(() => {
    if (onCloseRef.current) onCloseRef.current();
  }, []);

  const goFromModal = useCallback((route, params) => {
    if (onCloseRef.current) onCloseRef.current();
    const nav = navigationRef.current;
    if (nav && typeof nav.navigate === 'function') nav.navigate(route, params);
  }, []);
  const goVerify = useCallback(() => goFromModal(VERIFY_ROUTE), [goFromModal]);

  const loadOffer = useCallback((keepNotice = null, to = undefined) => {
    const seq = seqRef.current;
    if (to !== undefined) openToRef.current = to;
    setStage('loading');
    setConsent(false);
    setNotice(keepNotice);
    getMembershipOffer({ kind: targetKind, targetId })
      .then((o) => {
        if (seqRef.current !== seq) return;
        setOffer(o || null);
        setAnnounce(true);
        setConsent(false);
        // R6: a send the server's counter still refuses (J9r from the start).
        const eg = joinGatesOf(o).email;
        if (eg && eg.sendCooldownMs > 0) setSendBlockedUntil(Date.now() + eg.sendCooldownMs);
        setSelected(defaultChoice(joinChoicesOf(o), minTierRef.current, gatePerkRef.current));
        setSwitchSel(pickSwitch(o, openToRef.current, minTierRef.current, gatePerkRef.current));
        setStage(offerStage(o, openToRef.current));
      })
      .catch((err) => {
        if (seqRef.current !== seq) return;
        logMembershipError('getMembershipOffer', err);
        setError({ text: membershipErrorMessage(err) });
        setStage('offer_error');
      });
  }, [targetKind, targetId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Open / close. Closing (or a sign-out) drops everything; the checkout
  // effect's cleanup destroys a mounted form.
  useEffect(() => {
    seqRef.current += 1;
    clearTimeout(retryTimerRef.current);
    openToRef.current = openTo;
    setStage('idle');
    setOffer(null);
    setSelected(null);
    setConsent(false);
    setAnnounce(true);
    setBusy(false);
    busyRef.current = false;
    setError(null);
    setNotice(null);
    setRetryBlocked(false);
    setEmbedded(null);
    setJoinStartedAtMs(0);
    setJoinedPriceCents(0);
    setFinishingUp(false);
    setSwitchSel(null);
    setSwitchDone(null);
    setPendingPriceCents(null);
    setElement(null);
    setElementReady(false);
    setPayLine(null);
    setBankLine(false);
    joinRef.current = null;
    checkoutOnlyRef.current = bankAskedRecently(uid, bankKey);
    setEmailStep('ask');
    setEmailNote(null);
    setSentMasked(null);
    setEmailMissing(false);
    setSendBlockedUntil(0);
    setPollUntil(0);
    if (!visible || !IS_WEB || !uid || !targetId) return;
    loadOffer();
  }, [visible, uid, targetKind, targetId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => {
    seqRef.current += 1;
    clearTimeout(retryTimerRef.current);
  }, []);

  // A pending switch's price (channels only: a team has no tiers): the offer
  // carries the date and tier only, so read the member's own row (Your
  // memberships) once.
  const pendingTier = offer?.viewer?.nextTierKey || null;
  useEffect(() => {
    if (!visible || targetKind !== 'channel' || !pendingTier || !targetId) return undefined;
    let alive = true;
    listMyMemberships()
      .then((res) => {
        if (!alive) return;
        const row = (Array.isArray(res?.memberships) ? res.memberships : []).find((r) => r.creatorUid === targetId);
        const c = Number(row?.nextPriceCents);
        setPendingPriceCents(row && row.nextTierKey === pendingTier && c > 0 ? c : null);
      })
      .catch((err) => logMembershipError('listMyMemberships (pending price)', err));
    return () => { alive = false; };
  }, [visible, targetKind, targetId, pendingTier]);

  // Embedded checkout: keyed on the session only; callbacks via refs.
  const onCheckoutCompleteRef = useRef(null);
  onCheckoutCompleteRef.current = () => {
    // Paid (the form is already destroyed). The webhook records it; the
    // welcome waits on the pair doc.
    setJoinStartedAtMs(Date.now());
    setFinishingUp(false);
    setEmbedded(null);
    setStage('welcome');
  };
  const onCheckoutErrorRef = useRef(null);
  onCheckoutErrorRef.current = () => {
    setEmbedded(null);
    setStage('panel');
    setError({ text: TEXT.formFailed });
  };
  useEffect(() => {
    if (!embedded?.clientSecret) return undefined;
    return mountEmbeddedCheckout(checkoutBoxRef, embedded, onCheckoutCompleteRef, onCheckoutErrorRef);
  }, [embedded]);

  // "Finishing up" after a quiet wait in the welcome.
  useEffect(() => {
    if (stage !== 'welcome') return undefined;
    const t = setTimeout(() => setFinishingUp(true), FINISHING_UP_AFTER_MS);
    return () => clearTimeout(t);
  }, [stage, joinStartedAtMs]);

  // R6 pay stage: the Payment Element, keyed on the element checkout only
  // (callbacks via refs). Closing, a refusal that leaves the stage, or a
  // welcome drops `element`, and the cleanup destroys the form.
  const onElementReadyRef = useRef(null);
  onElementReadyRef.current = () => setElementReady(true);
  const onElementErrorRef = useRef(null);
  onElementErrorRef.current = () => {
    setElement(null);
    setElementReady(false);
    setStage('panel');
    setError({ text: TEXT.formFailed });
  };
  useEffect(() => {
    if (!element?.checkoutId) return undefined;
    const ctl = mountMembershipElement(payBoxRef, element, onElementReadyRef, onElementErrorRef);
    payCtlRef.current = ctl;
    return () => {
      if (payCtlRef.current === ctl) payCtlRef.current = null;
      ctl.destroy();
    };
  }, [element]);

  // R6 email gate: re-check on window focus, and every 5 seconds for 2
  // minutes after a send (an Auth reload; once confirmed, the offer again,
  // which reads Auth on the server). "I've confirmed it" stays the fallback.
  const gatesNow = stage === 'panel' ? joinGatesOf(offer) : { email: null, wait: null };
  const emailGateShown = !!gatesNow.email;
  useEffect(() => {
    if (!visible || !emailGateShown) return undefined;
    let alive = true;
    const check = async () => {
      if (busyRef.current) return;
      const confirmed = await reloadEmailVerified();
      if (alive && confirmed === true) loadOffer();
    };
    const onFocus = () => { check(); };
    const canListen = IS_WEB && typeof window !== 'undefined' && typeof window.addEventListener === 'function';
    if (canListen) window.addEventListener('focus', onFocus);
    let timer = null;
    if (pollUntil > Date.now()) {
      timer = setInterval(() => {
        if (Date.now() > pollUntil) { clearInterval(timer); return; }
        check();
      }, EMAIL_POLL_EVERY_MS);
    }
    return () => {
      alive = false;
      if (canListen) window.removeEventListener('focus', onFocus);
      if (timer) clearInterval(timer);
    };
  }, [visible, emailGateShown, pollUntil, loadOffer]);

  // R6 wait (J10): the offer again once the join time passes.
  const waitAtMs = gatesNow.email ? null : (gatesNow.wait?.joinAtMs || null);
  useEffect(() => {
    if (!visible || !waitAtMs) return undefined;
    const left = waitAtMs - Date.now();
    const t = setTimeout(() => loadOffer(), Math.min(Math.max(1000, left + 1000), MAX_TIMER_MS));
    return () => clearTimeout(t);
  }, [visible, waitAtMs, loadOffer]);

  // J9r counts down (a re-render each half minute, and right when it ends).
  useEffect(() => {
    const left = sendBlockedUntil - Date.now();
    if (!(left > 0)) return undefined;
    const t = setTimeout(() => setCooldownTick((n) => n + 1), Math.max(1000, Math.min(30000, left + 250)));
    return () => clearTimeout(t);
  }, [sendBlockedUntil, cooldownTick]);

  const choices = joinChoicesOf(offer);
  const tiered = choices.length > 0 && choices[0].tierKey !== null;
  // No tier unlocks the opening gate: the tiers are listed with nothing
  // picked, and Continue waits for the fan's pick (ADV2-2).
  const gateUnreached = joinGateUnreached(choices, minTierRef.current, gatePerkRef.current);
  const choice = choices.find((c) => c.tierKey === selected) || (gateUnreached ? null : choices[0]) || null;
  const tierListShown = choices.length > 1 || gateUnreached;
  // The announce option (R5: announceOption; an older server: perks.joinLine).
  const announceOffered = offer?.announceOption === true
    || (offer?.announceOption === undefined && offer?.perks?.joinLine === true);
  const showAnnounce = announceOffered && offer?.viewer?.canAnnounce === true;
  const limit = offer?.limit && typeof offer.limit === 'object' ? offer.limit : null;
  const verify = limit?.verify;

  const pickTier = (tierKey) => {
    if (busy || tierKey === selected) return;
    setSelected(tierKey);
    setConsent(false);
    setError(null);
  };

  // The payment step createMembershipCheckout answered with. j: the join's
  // own price and disclosure, the fallbacks. Stage first: a form that fails
  // to open sends the payer back to the panel, and that must win.
  const enterPaymentStep = (data, j) => {
    setJoinedPriceCents(Number(data.amountCents) > 0 ? Number(data.amountCents) : j.priceCents);
    const disclosure = typeof data.disclosure === 'string' && data.disclosure ? data.disclosure : j.disclosure;
    if (data.flow === 'element') {
      setPayLine(null);
      setElementReady(false);
      setStage('pay');
      setElement({ ...data, disclosure });
      return;
    }
    setStage('checkout');
    setEmbedded({ clientSecret: data.clientSecret, publishableKey: data.publishableKey, disclosure });
  };

  // A refusal of createMembershipCheckout, on the panel (o: the offer it
  // was made from; seq: the opening).
  const onCheckoutStartError = (err, o, seq) => {
    const kind = membershipErrorKind(err);
    const sentence = membershipErrorMessage(err);
    switch (kind) {
      case 'terms':
        setError(termsError(o));
        break;
      case 'consent':
        setConsent(false);
        setError({ text: sentence });
        break;
      case 'price_changed':
      case 'closed':
        // The new prices (or tiers), disclosure and consent text, with the
        // box unticked.
        loadOffer(sentence);
        break;
      case 'limit':
        // 'limits' mode: the room moved; the reloaded offer shows it.
        // 'cap' mode keeps today's sentence.
        if (limit) loadOffer(sentence);
        else setError({ text: sentence });
        break;
      case 'canceling':
      case 'past_due':
      case 'exists':
        setError({ text: sentence, action: 'memberships' });
        break;
      case 'processing':
        // Never the welcome: nothing was paid in this session.
        setStage('processing');
        break;
      case 'email':
      case 'wait':
        // R6: the server refused after the offer said yes (a race): the
        // reloaded offer carries the email gate (J7) or the wait (J10).
        loadOffer();
        break;
      case 'busy':
        setError({ text: sentence });
        setRetryBlocked(true);
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = setTimeout(() => {
          if (seqRef.current === seq) setRetryBlocked(false);
        }, BUSY_RETRY_MS);
        break;
      default:
        setError({ text: sentence });
    }
  };

  const startCheckout = async () => {
    if (busyRef.current || !consent || retryBlocked || stage !== 'panel') return;
    const o = offer;
    const c = choice;
    if (!o || !c || c.withinLimit === false) return;
    const seq = seqRef.current;
    const stillOpen = () => seqRef.current === seq;
    busyRef.current = true;
    setBusy(true);
    setError(null);
    setNotice(null);
    setBankLine(false);
    const join = {
      args: {
        kind: targetKind,
        targetId,
        tierKey: c.tierKey || undefined,
        announce: showAnnounce ? announce === true : false,
        renewalConsent: { accepted: true, amountCents: c.priceCents, tierKey: c.tierKey || undefined },
      },
      priceCents: c.priceCents,
      disclosure: c.disclosure,
    };
    try {
      const data = await createMembershipCheckout({
        ...join.args,
        // R6 (3.13.2): our own payment step; the server may still answer
        // with Embedded Checkout (flow 'checkout'). After the bank wanted to
        // confirm a payment in this opening: Embedded Checkout directly.
        flow: checkoutOnlyRef.current ? 'checkout' : 'element',
      });
      if (!stillOpen()) return;
      joinRef.current = join;
      enterPaymentStep(data, join);
    } catch (err) {
      logMembershipError('createMembershipCheckout', err);
      if (!stillOpen()) return;
      onCheckoutStartError(err, o, seq);
    } finally {
      busyRef.current = false;
      if (stillOpen()) setBusy(false);
    }
  };

  // ------------------------------------------------------------------
  // R6 pay stage (3.13.2): the tap makes a ConfirmationToken (nothing is
  // charged); confirmMembershipJoin checks the card before any charge and
  // pays on the server. When the bank wants to confirm the payment, the
  // same join continues on Embedded Checkout (founder decision 10-18).
  // ------------------------------------------------------------------
  const leavePayStage = () => {
    setElement(null);
    setElementReady(false);
    setPayLine(null);
  };

  const applyJoinAction = (action, startedAtMs) => {
    if (action.kind === 'welcome') {
      leavePayStage();
      setJoinStartedAtMs(startedAtMs);
      setFinishingUp(false);
      setStage('welcome');
      return;
    }
    if (action.kind === 'line') {
      setPayLine({ id: action.id, text: action.text, button: null, tryAgain: action.tryAgain === true, anotherCard: action.anotherCard === true });
    }
    // 'ready': nothing reached Stripe; the pay button is back.
  };

  // The bank wants to confirm the payment: nothing was charged and the
  // server closed the element checkout. The approved line shows at once,
  // and the same join continues on Embedded Checkout, where Stripe runs the
  // bank's check. A refusal of the new checkout lands on the panel.
  const continueOnCheckout = async (join, seq) => {
    const stillOpen = () => seqRef.current === seq;
    checkoutOnlyRef.current = true;
    leavePayStage();
    setBankLine(true);
    setEmbedded(null);
    setStage('checkout');
    try {
      const data = await createMembershipCheckout({ ...join.args, flow: 'checkout' });
      if (!stillOpen()) return;
      enterPaymentStep(data, join);
    } catch (err) {
      logMembershipError('createMembershipCheckout (continue on Checkout)', err);
      if (!stillOpen()) return;
      setStage('panel');
      onCheckoutStartError(err, offer, seq);
    }
  };

  const onPayError = (err) => {
    const kind = membershipErrorKind(err);
    // J4 / J5 / J5L: refused before any charge; the stage stays open.
    const card = cardRefusalLine(kind);
    if (card) {
      const v = card.verifyClause ? withVerify(card.text, err?.details?.verify) : { text: card.text, button: null };
      setPayLine({ id: card.id, text: v.text, button: v.button || null, tryAgain: false, anotherCard: true });
      return;
    }
    switch (kind) {
      case 'email':
      case 'wait':
        leavePayStage();
        loadOffer();
        break;
      case 'checkout':
      case 'closed':
      case 'price_changed':
        // The checkout can no longer be paid (it expired or was closed, the
        // channel stopped taking members, the price moved): back to the
        // panel to start again, the box unticked.
        leavePayStage();
        loadOffer(membershipErrorMessage(err));
        break;
      case 'terms':
        leavePayStage();
        setStage('panel');
        setError(termsError(offer));
        break;
      default:
        setPayLine({ id: 'error', text: membershipErrorMessage(err), button: null, tryAgain: false, anotherCard: false });
    }
  };

  const payNow = async () => {
    const ctl = payCtlRef.current;
    const el = element;
    const join = joinRef.current;
    if (busyRef.current || stage !== 'pay' || !el?.checkoutId || !ctl || !elementReady || !join) return;
    const seq = seqRef.current;
    const stillOpen = () => seqRef.current === seq;
    const startedAtMs = Date.now();
    busyRef.current = true;
    setBusy(true);
    setPayLine(null);
    try {
      const tok = await ctl.createToken();
      if (!stillOpen()) return;
      if (tok.error) {
        // Field errors show inside the form itself.
        if (!tok.validation) {
          const e = tok.error;
          const text = (e.type === 'card_error' || e.type === 'validation_error') && typeof e.message === 'string' && e.message
            ? e.message : GENERIC_FAILURE;
          setPayLine({ id: 'stripe', text, button: null, tryAgain: false, anotherCard: false });
        }
        return;
      }
      const args = { checkoutId: el.checkoutId, confirmationToken: tok.tokenId };
      const action = joinResultAction(await confirmMembershipJoin(args));
      if (action.kind === 'continue_checkout') rememberBankAsk(uid, bankKey);
      if (!stillOpen()) return;
      if (action.kind === 'continue_checkout') {
        await continueOnCheckout(join, seq);
        return;
      }
      applyJoinAction(action, startedAtMs);
    } catch (err) {
      logMembershipError('confirmMembershipJoin', err);
      if (!stillOpen()) return;
      onPayError(err);
    } finally {
      busyRef.current = false;
      if (stillOpen()) setBusy(false);
    }
  };

  const pickAnotherCard = () => {
    if (busyRef.current) return;
    setPayLine(null);
    if (payCtlRef.current) payCtlRef.current.clear();
  };

  // ------------------------------------------------------------------
  // R6 email gate (3.13.3): our own confirmation email, then the re-check.
  // ------------------------------------------------------------------
  const sendConfirmation = async () => {
    if (busyRef.current || stage !== 'panel' || sendBlockedUntil > Date.now()) return;
    const seq = seqRef.current;
    const stillOpen = () => seqRef.current === seq;
    busyRef.current = true;
    setBusy(true);
    setEmailNote(null);
    try {
      const res = await sendEmailConfirmation(targetKind === 'team'
        ? { returnTo: 'verification' }
        : { returnTo: 'join', creatorUid: targetId });
      if (!stillOpen()) return;
      if (res?.alreadyConfirmed === true) {
        await reloadEmailVerified();
        if (stillOpen()) loadOffer();
        return;
      }
      if (typeof res?.maskedEmail === 'string' && res.maskedEmail) setSentMasked(res.maskedEmail);
      setEmailStep('sent');
      const cd = Number(res?.cooldownMs) || 0;
      setSendBlockedUntil(cd > 0 ? Date.now() + cd : 0);
      setPollUntil(Date.now() + EMAIL_POLL_FOR_MS);
    } catch (err) {
      logMembershipError('sendEmailConfirmation', err);
      if (!stillOpen()) return;
      const retryAtMs = Number(err?.details?.retryAtMs);
      if (membershipErrorKind(err) === 'email_limit' && Number.isFinite(retryAtMs) && retryAtMs > Date.now()) {
        setSendBlockedUntil(retryAtMs);
      } else if (membershipErrorKind(err) === 'email_missing') {
        // R6 review R6C-4: no sign-in address, so no send can work; J9w
        // under the panel already names support (never the line twice).
        setEmailMissing(true);
      } else {
        setEmailNote(membershipErrorMessage(err));
      }
    } finally {
      busyRef.current = false;
      if (stillOpen()) setBusy(false);
    }
  };

  // "I've confirmed it": J9 when the account is still not confirmed;
  // otherwise (or when the reload fails) the offer again, which the server
  // answers from the Auth record.
  const checkConfirmed = async (maskedEmail) => {
    if (busyRef.current || stage !== 'panel') return;
    const seq = seqRef.current;
    const stillOpen = () => seqRef.current === seq;
    busyRef.current = true;
    setBusy(true);
    setEmailNote(null);
    try {
      const confirmed = await reloadEmailVerified();
      if (!stillOpen()) return;
      if (confirmed === false) {
        setEmailNote(notConfirmedYetLine(maskedEmail));
        return;
      }
      loadOffer();
    } finally {
      busyRef.current = false;
      if (stillOpen()) setBusy(false);
    }
  };

  // The switch (or "Keep {From}"). Nothing is charged; the server records
  // the consent and swaps the price at the next renewal.
  const confirmSwitch = async () => {
    const opts = switchOptionsOf(offer);
    const opt = opts.find((s) => s.tierKey === switchSel);
    if (busyRef.current || !consent || !opt || stage !== 'switch') return;
    if (opt.kind === 'switch' && limit && opt.withinLimit === false) return;
    const seq = seqRef.current;
    const stillOpen = () => seqRef.current === seq;
    const v = offer?.viewer || {};
    const fromName = tierNameOf(v.tierKey) || 'Member';
    busyRef.current = true;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const res = await switchMyMembershipTier({
        creatorUid: targetId,
        tierKey: opt.tierKey,
        consent: { accepted: true, amountCents: opt.priceCents, tierKey: opt.tierKey },
      });
      if (!stillOpen()) return;
      const atMs = Number(res?.effectiveAtMs) || Number(v.renewsAtMs) || 0;
      setSwitchDone({
        text: (res?.kind || opt.kind) === 'stay'
          ? stayDoneText(fromName)
          : switchDoneText(tierNameOf(res?.tierKey) || tierNameOf(opt.tierKey), longDate(atMs)),
      });
      setStage('switched');
      if (onChangedRef.current) onChangedRef.current();
    } catch (err) {
      logMembershipError('switchMyMembershipTier', err);
      if (!stillOpen()) return;
      const kind = membershipErrorKind(err);
      const sentence = membershipErrorMessage(err);
      switch (kind) {
        case 'consent':
          setConsent(false);
          setError({ text: sentence });
          break;
        case 'price_changed':
        case 'closed':
          loadOffer(sentence, 'switch');
          break;
        case 'limit':
          if (limit) loadOffer(sentence, 'switch');
          else setError({ text: sentence });
          break;
        case 'terms':
          setError(termsError(offer));
          break;
        case 'canceling':
        case 'past_due':
        case 'not_found':
          setError({ text: sentence, action: 'memberships' });
          break;
        default:
          setError({ text: sentence });
      }
    } finally {
      busyRef.current = false;
      if (stillOpen()) setBusy(false);
    }
  };

  const refreshPage = () => {
    if (IS_WEB && typeof window !== 'undefined') window.location.reload();
  };

  // ------------------------------------------------------------------
  // Pieces
  // ------------------------------------------------------------------
  const primary = (label, onPress, opts = {}) => (
    <TouchableOpacity
      style={[styles.primaryBtn, opts.disabled && styles.btnDisabled]}
      onPress={onPress}
      disabled={opts.disabled === true}
      accessibilityRole="button"
      accessibilityState={opts.disabled ? { disabled: true } : undefined}
    >
      {opts.busy ? <ActivityIndicator size="small" color="#0b0b0b" /> : <Text style={styles.primaryBtnText}>{label}</Text>}
    </TouchableOpacity>
  );

  const secondary = (label, onPress) => (
    <TouchableOpacity style={styles.secondaryBtn} onPress={onPress} accessibilityRole="button">
      <Text style={styles.secondaryBtnText}>{label}</Text>
    </TouchableOpacity>
  );

  const errorAction = (err) => {
    if (!err?.action) return null;
    if (err.action === 'refresh') return primary('Refresh', refreshPage);
    if (err.action === 'review_terms') return primary('Review Terms', () => goFromModal('TosAgreement'));
    if (err.action === 'memberships') return primary('Your memberships', () => goFromModal('Memberships'));
    return null;
  };

  const title = (text) => <Text style={styles.title}>{text}</Text>;
  const line = (text, style) => (text ? <Text style={[styles.sub, style]}>{text}</Text> : null);

  const radio = (on) => (
    <Ionicons name={on ? 'radio-button-on' : 'radio-button-off'} size={18} color={on ? COLORS.GOLD : '#666'} style={styles.radioIcon} />
  );

  // ------------------------------------------------------------------
  // Stages
  // ------------------------------------------------------------------
  const renderExisting = (state, v) => {
    if (state === 'activating') {
      return (
        <>
          {title(copy.title(name))}
          {line(TEXT.activating)}
          {secondary('Done', requestClose)}
        </>
      );
    }
    let text = copy.existing(name);
    if (state === 'canceling') {
      const date = formatMembershipDate(v?.endsAtMs);
      text = date ? `Your membership ends on ${date}.` : text;
    }
    if (state === 'past_due') text = TEXT.pastDue;
    // F5: the member's tier and price, and a pending switch.
    const tierName = tierNameOf(v?.tierKey);
    const price = Number(v?.periodPriceCents) || 0;
    const nextName = tierNameOf(v?.nextTierKey);
    // A team has no tiers (3.5b): never Switch tier or Keep {From} for it.
    const opts = state === 'active' && targetKind !== 'team' ? switchOptionsOf(offer) : [];
    const canSwitch = opts.some((s) => s.kind === 'switch');
    const stay = opts.find((s) => s.kind === 'stay');
    const openSwitch = (want) => {
      setSwitchSel(pickSwitch(offer, want, minTierRef.current, gatePerkRef.current));
      setConsent(false);
      setError(null);
      setStage('switch');
    };
    return (
      <>
        {title(copy.memberTitle(name))}
        {line(text)}
        {tierName && price > 0 ? <Text style={styles.tierLine}>{tierPriceLine(tierName, price)}</Text> : null}
        {nextName && Number(pendingPriceCents) > 0 ? (
          <Text style={styles.pendingLine}>{pendingSwitchLine(nextName, longDate(v.nextTierAtMs), pendingPriceCents)}</Text>
        ) : null}
        {notice ? <Text style={styles.noticeText}>{notice}</Text> : null}
        {canSwitch ? primary(SWITCH_TIER_LABEL, () => openSwitch('switch')) : null}
        {stay ? (
          <TouchableOpacity style={styles.outlineBtn} onPress={() => openSwitch('stay')} accessibilityRole="button">
            <Text style={styles.outlineBtnText}>{keepTierLabel(tierNameOf(stay.tierKey) || 'Member')}</Text>
          </TouchableOpacity>
        ) : null}
        {canSwitch || stay
          ? secondary('Your memberships', () => goFromModal('Memberships'))
          : primary('Your memberships', () => goFromModal('Memberships'))}
        {secondary('Done', requestClose)}
      </>
    );
  };

  const renderUnavailable = () => {
    if (offer?.reasonToken === 'terms-required') {
      const t = termsError(offer);
      return (
        <>
          {title(copy.title(name))}
          {line(t.text)}
          {errorAction(t)}
          {secondary('Not now', requestClose)}
        </>
      );
    }
    // At the 24-hour limit in 'limits' mode: L3m / L3bm / L3pm / L4m with
    // the cooldown, re-checked when it ends. 'cap' mode sends no limit and
    // keeps the server's sentence.
    const atLimit = offer?.reasonToken === 'membership-limit' && limit ? limitLines(limit, { surface: 'join' }) : null;
    if (atLimit && atLimit.atLimit) {
      return (
        <>
          {title(copy.title(name))}
          <SpendingLimitNotice
            limit={limit}
            surface="join"
            onExpire={() => loadOffer()}
            onVerify={goVerify}
            style={styles.limitBox}
          />
          {secondary('Not now', requestClose)}
        </>
      );
    }
    const reason = typeof offer?.reason === 'string' && offer.reason.trim() ? offer.reason.trim() : copy.closedFallback;
    return (
      <>
        {title(copy.title(name))}
        {line(reason)}
        {secondary('Not now', requestClose)}
      </>
    );
  };

  // One tier's perk lines (F2), or the one-price panel's perk labels.
  const perkList = (lines) => (
    <View style={styles.perks}>
      {lines.map((text) => (
        <View key={text} style={styles.perkRow}>
          <Ionicons name="star" size={13} color={COLORS.GOLD} style={styles.perkIcon} />
          <Text style={styles.perkText}>{text}</Text>
        </View>
      ))}
    </View>
  );

  const renderPanel = () => {
    const o = offer || {};
    const c = choice;
    if (!c && !gateUnreached) return renderUnavailable();
    const continueDisabled = busy || !c || !consent || retryBlocked || c.withinLimit === false;
    const lines = tiered ? tierPerkLines(choices) : [];
    const linesOf = (tierKey) => (lines.find((l) => l.tierKey === tierKey) || { lines: [] }).lines;
    const room = limit && limit.limited === true ? Math.max(0, Number(limit.remainingCents) || 0) : null;
    const anyVideos = choices.some((t) => t.perks?.membersVideos === true);
    const refused = o.viewer?.lastJoinRefused?.reason
      ? joinRefusedLine(o.viewer.lastJoinRefused.reason, { verify })
      : null;
    // R6 (3.13.3): the email gate first, then the wait, in place of the box
    // and Continue (the server refuses the checkout in the same order).
    const gates = joinGatesOf(o);
    const waitLine = !gates.email && gates.wait
      ? waitPanelLine({
        hours: gates.wait.minAccountAgeHours,
        dateTime: joinAtText(gates.wait.joinAtMs),
        verify: verifyLine(verify),
        kind: targetKind,
      })
      : null;
    const gated = !!gates.email || !!waitLine;
    return (
      <>
        {title(copy.title(name))}
        {tierListShown ? line(joinLead(name)) : line(copy.support(name))}

        {/* L1m: an unverified payer's room ('limits' mode only). */}
        {limit ? (
          <SpendingLimitNotice
            limit={limit}
            surface="join"
            amountCents={c ? c.priceCents : null}
            onExpire={() => loadOffer()}
            onVerify={goVerify}
            disabled={busy}
            style={styles.limitBox}
          />
        ) : null}

        {tierListShown ? (
          <View style={styles.tierList} accessibilityRole="radiogroup">
            {choices.map((t) => {
              const on = !!c && t.tierKey === c.tierKey;
              const outside = t.withinLimit === false;
              return (
                <TouchableOpacity
                  key={t.tierKey}
                  style={[styles.tierRow, on && styles.tierRowOn, outside && styles.tierRowOff]}
                  onPress={() => pickTier(t.tierKey)}
                  disabled={busy || outside}
                  accessibilityRole="radio"
                  accessibilityState={{ selected: on, disabled: busy || outside }}
                  accessibilityLabel={`${tierNameOf(t.tierKey)}, ${formatMonthlyPrice(t.priceCents)}`}
                >
                  <View style={styles.tierTop}>
                    {radio(on)}
                    <Text style={styles.tierName}>{tierNameOf(t.tierKey)}</Text>
                    <Text style={styles.tierPrice}>{formatMonthlyPrice(t.priceCents)}</Text>
                  </View>
                  {perkList(linesOf(t.tierKey))}
                  {outside && room !== null ? <Text style={styles.tierLimitText}>{LINES.L2m(formatCurrency(room))}</Text> : null}
                </TouchableOpacity>
              );
            })}
          </View>
        ) : (
          <>
            {perkList(tiered
              ? linesOf(c.tierKey)
              : listedPerks.filter((k) => c.perks?.[k] === true).map((k) => perkLabels[k]))}
          </>
        )}
        {tiered && anyVideos ? <Text style={styles.footnote}>{VIDEOS_FOOTER}</Text> : null}

        {o.viewer?.lastAttemptFailed === true ? <Text style={styles.warnText}>{TEXT.lastAttemptFailed}</Text> : null}
        {tierListShown ? null : (
          <>
            {tiered ? <Text style={styles.tierAbovePrice}>{tierNameOf(c.tierKey)}</Text> : null}
            <Text style={[styles.price, tiered && styles.priceUnderTier]}>{formatMonthlyPrice(c.priceCents)}</Text>
          </>
        )}

        {showAnnounce ? (
          <View style={styles.toggleRow}>
            <Text style={styles.toggleText}>{copy.announce(name)}</Text>
            <Switch
              value={announce}
              onValueChange={setAnnounce}
              disabled={busy}
              trackColor={{ false: '#333', true: '#7a6500' }}
              thumbColor={announce ? COLORS.GOLD : '#888'}
              accessibilityLabel={copy.announce(name)}
            />
          </View>
        ) : null}

        {notice ? <Text style={styles.noticeText}>{notice}</Text> : null}

        {/* L10c / L10d: for 24 h after a join refused for its card. */}
        {refused ? <SpendingLimitNotice line={refused} onVerify={goVerify} disabled={busy} style={styles.limitBox} /> : null}

        {c ? (
          <>
            <Text style={styles.disclosure}>{c.disclosure}</Text>
            {gated ? null : (
              <ConsentCheckbox
                checked={consent}
                onToggle={(next) => { setConsent(next === true); }}
                label={c.consentText}
                disabled={busy}
              />
            )}
          </>
        ) : null}

        {/* R6: J7 / J8 / J9 (email gate) or J10 (wait). */}
        {gates.email ? renderEmailGate(gates.email) : null}
        {waitLine ? <SpendingLimitNotice line={waitLine} onVerify={goVerify} disabled={busy} style={styles.limitBox} /> : null}

        <Text style={styles.termsLine}>
          {'By joining you agree to the '}
          <Text
            style={styles.termsLink}
            accessibilityRole="link"
            onPress={() => openTermsOfService(navigationRef.current, { onBeforeNavigate: requestClose })}
          >
            Terms of Service
          </Text>
          .
        </Text>

        {error ? <Text style={styles.errorText}>{error.text}</Text> : null}
        {errorAction(error)}

        {gated ? null : (
          <TouchableOpacity
            style={[styles.primaryBtn, continueDisabled && styles.btnDisabled]}
            onPress={startCheckout}
            disabled={continueDisabled}
            accessibilityRole="button"
            accessibilityState={{ disabled: !consent }}
          >
            {busy ? (
              <ActivityIndicator size="small" color="#0b0b0b" />
            ) : (
              <Text style={styles.primaryBtnText}>Continue to payment</Text>
            )}
          </TouchableOpacity>
        )}
        {c && !gated ? <ConsentHint visible={!consent} /> : null}
        {/* J1: the statement line, every payer. */}
        <Text style={styles.footnote}>{STATEMENT_LINE}</Text>
        {secondary('Not now', requestClose)}
      </>
    );
  };

  // J7 (ask) / J8 (sent), J9 or a refusal under it, J9r while sends are
  // refused, and J9w. The address is always the masked one.
  const renderEmailGate = (eg) => {
    const masked = sentMasked || eg.maskedEmail || maskEmail(auth.currentUser?.email);
    const left = sendBlockedUntil - Date.now();
    const blocked = left > 0;
    const sent = emailStep === 'sent' && !!masked;
    // R6 review R6C-4: an account with no address gets no Send button (it
    // could never work), only one support line: the no-address line (R6 copy
    // read 13.2 V2, the default applied 10-18) in place of J9w.
    const noAddress = emailMissing || !masked;
    return (
      <View style={styles.gateBox}>
        <Text style={styles.gateText}>{sent ? emailSentLine(masked) : emailGateLead(masked)}</Text>
        {emailNote ? <Text style={styles.noticeText}>{emailNote}</Text> : null}
        {blocked ? <Text style={styles.noticeText}>{sendLimitLine(formatCooldown(left))}</Text> : null}
        {sent ? (
          <>
            {primary(CONFIRMED_IT_LABEL, () => checkConfirmed(masked), { disabled: busy, busy })}
            <TouchableOpacity
              style={[styles.outlineBtn, (busy || blocked) && styles.btnDisabled]}
              onPress={sendConfirmation}
              disabled={busy || blocked}
              accessibilityRole="button"
              accessibilityState={busy || blocked ? { disabled: true } : undefined}
            >
              <Text style={styles.outlineBtnText}>{SEND_AGAIN_LABEL}</Text>
            </TouchableOpacity>
          </>
        ) : (noAddress ? null : primary(SEND_CONFIRMATION_LABEL, sendConfirmation, { disabled: busy || blocked, busy }))}
        <Text style={styles.footnote}>{noAddress && !sent ? NO_ADDRESS_LINE : WRONG_ADDRESS_LINE}</Text>
      </View>
    );
  };

  // R6 pay stage: J0, the disclosure, the Payment Element, the line a
  // refusal or decline left (J4, J5, J5a, J5d, J5L) and J3.
  const renderPayLine = () => {
    if (!payLine?.text) return null;
    const b = payLine.button;
    return (
      <View style={styles.payLineBox}>
        <Text style={styles.payLineText}>{payLine.text}</Text>
        {payLine.tryAgain || payLine.anotherCard || b ? (
          <View style={styles.payLineBtns}>
            {payLine.tryAgain ? (
              <TouchableOpacity style={[styles.smallOutlineBtn, busy && styles.btnDisabled]} onPress={payNow} disabled={busy} accessibilityRole="button">
                <Text style={styles.smallOutlineText}>{TRY_AGAIN_LABEL}</Text>
              </TouchableOpacity>
            ) : null}
            {payLine.anotherCard ? (
              <TouchableOpacity style={[styles.smallOutlineBtn, busy && styles.btnDisabled]} onPress={pickAnotherCard} disabled={busy} accessibilityRole="button">
                <Text style={styles.smallOutlineText}>{USE_ANOTHER_CARD_LABEL}</Text>
              </TouchableOpacity>
            ) : null}
            {b && b.kind === 'verify' ? (
              <TouchableOpacity
                style={[styles.smallGoldBtn, busy && styles.btnDisabled]}
                onPress={goVerify}
                disabled={busy}
                accessibilityRole="button"
                accessibilityLabel={b.accessibilityLabel}
              >
                <Text style={styles.smallGoldText}>{b.label}</Text>
              </TouchableOpacity>
            ) : null}
            {b && b.kind === 'support' ? (
              <TouchableOpacity style={styles.smallOutlineBtn} onPress={() => { Linking.openURL(b.href).catch(() => {}); }} accessibilityRole="link">
                <Text style={styles.smallOutlineText}>{b.label}</Text>
              </TouchableOpacity>
            ) : null}
          </View>
        ) : null}
      </View>
    );
  };

  const renderPay = () => {
    const el = element || {};
    const tierName = el.tierLabel || el.tierName || tierNameOf(el.tierKey);
    const forName = typeof el.creatorName === 'string' && el.creatorName.trim() ? el.creatorName.trim() : name;
    // A team (3.5b) has no tier: J0 names the team (W1), from the element
    // answer's targetName when it carries one, else the modal's own noun.
    const payTitle = targetKind === 'team'
      ? teamPayStageTitle(isStr(el.targetName) && el.targetName.trim() ? teamDisplayName(el.targetName) : name)
      : payStageTitle(tierName, forName);
    return (
      <>
        {title(payTitle)}
        <Text style={[styles.disclosure, styles.disclosureAboveForm]}>{el.disclosure || choice?.disclosure}</Text>
        {/* RN Web: the View ref is the DOM node Stripe mounts the Payment Element into. */}
        <View ref={payBoxRef} style={styles.elementBox} />
        {elementReady ? null : <ActivityIndicator style={styles.elementLoading} color={COLORS.GOLD} />}
        {renderPayLine()}
        {primary(payButtonLabel(el.amountCents || joinedPriceCents), payNow, { disabled: busy || !elementReady, busy })}
        {secondary('Not now', requestClose)}
      </>
    );
  };

  // The continuation (bankLine): the approved line from the moment the bank
  // wants to confirm, while the session opens (a spinner), and above
  // Stripe's form.
  const renderCheckout = () => (
    <>
      {title(copy.title(name))}
      {bankLine ? (
        <View style={styles.gateBox} role="alert" aria-live="polite">
          <Text style={styles.gateText}>{BANK_CONTINUE_LINE}</Text>
        </View>
      ) : null}
      <Text style={[styles.disclosure, styles.disclosureAboveForm]}>{embedded?.disclosure || choice?.disclosure}</Text>
      {/* RN Web: the View ref is the DOM node Stripe mounts its iframe into. */}
      {embedded ? <View ref={checkoutBoxRef} style={styles.checkoutBox} /> : <ActivityIndicator style={styles.loading} color={COLORS.GOLD} />}
      {secondary('Not now', requestClose)}
    </>
  );

  const renderSwitch = () => {
    const v = offer?.viewer || {};
    const opts = switchOptionsOf(offer);
    if (!opts.length) return renderExisting(v.state, v);
    const fromName = tierNameOf(v.tierKey) || 'Member';
    const renewsAtMs = Number(v.renewsAtMs) || 0;
    const stay = opts.find((s) => s.kind === 'stay');
    const sel = opts.find((s) => s.tierKey === switchSel) || null;
    const nextName = tierNameOf(v.nextTierKey);
    const selBlocked = !!sel && sel.kind === 'switch' && !!limit && sel.withinLimit === false;
    const disabled = busy || !consent || !sel || selBlocked;
    const pick = (tierKey) => {
      if (busy || tierKey === switchSel) return;
      setSwitchSel(tierKey);
      setConsent(false);
      setError(null);
    };
    // The current tier: the "Keep {From}" option while a switch is pending,
    // else a plain row with its chip.
    const current = stay || (isTierKey(v.tierKey)
      ? { tierKey: v.tierKey, priceCents: Number(v.periodPriceCents) || 0, kind: 'current' }
      : null);
    const rows = [...(current ? [current] : []), ...opts.filter((s) => s.kind === 'switch')];
    return (
      <>
        {title(SWITCH_TITLE)}
        {line(switchLead(fromName, name, longDate(renewsAtMs)))}
        {nextName && Number(pendingPriceCents) > 0 ? (
          <Text style={styles.pendingLine}>{pendingSwitchLine(nextName, longDate(v.nextTierAtMs), pendingPriceCents)}</Text>
        ) : null}
        <View style={styles.tierList} accessibilityRole="radiogroup">
          {rows.map((r) => {
            const selectable = r.kind !== 'current';
            const on = selectable && r.tierKey === switchSel;
            const outside = r.kind === 'switch' && !!limit && r.withinLimit === false;
            const tName = tierNameOf(r.tierKey);
            const l2s = outside
              ? switchLimitLine({ toName: tName, diffCents: r.countsCents, remainingCents: limit.remainingCents, verify })
              : null;
            return (
              <TouchableOpacity
                key={`${r.kind}:${r.tierKey}`}
                style={[styles.tierRow, on && styles.tierRowOn, (outside || !selectable) && styles.tierRowOff]}
                onPress={() => pick(r.tierKey)}
                disabled={busy || outside || !selectable}
                accessibilityRole="radio"
                accessibilityState={{ selected: on, disabled: busy || outside || !selectable }}
                accessibilityLabel={r.priceCents > 0 ? `${tName}, ${formatMonthlyPrice(r.priceCents)}` : tName}
              >
                <View style={styles.tierTop}>
                  {selectable ? radio(on) : <View style={styles.radioIcon} />}
                  <Text style={styles.tierName}>{tName}</Text>
                  {r.tierKey === v.tierKey ? <Text style={styles.currentChip}>{CURRENT_TIER_CHIP}</Text> : null}
                  {r.priceCents > 0 ? <Text style={styles.tierPrice}>{formatMonthlyPrice(r.priceCents)}</Text> : null}
                </View>
                {l2s ? <Text style={styles.tierLimitText}>{l2s.text}</Text> : null}
              </TouchableOpacity>
            );
          })}
        </View>

        {notice ? <Text style={styles.noticeText}>{notice}</Text> : null}

        {/* F4c: what a switch up counts toward an unverified payer's 24 hours
            ('limits' mode only; 'cap' sends no limit). */}
        {sel && sel.kind === 'switch' && limit && Number(sel.countsCents) > 0 && sel.withinLimit !== false ? (
          <SpendingLimitNotice
            line={switchCountLine({ diffCents: sel.countsCents, verify })}
            onVerify={goVerify}
            disabled={busy}
            style={styles.limitBox}
          />
        ) : null}

        {sel ? (
          <>
            <Text style={styles.disclosure}>{sel.disclosure}</Text>
            <ConsentCheckbox
              checked={consent}
              onToggle={(next) => { setConsent(next === true); }}
              label={sel.consentText}
              disabled={busy}
            />
          </>
        ) : null}

        {error ? <Text style={styles.errorText}>{error.text}</Text> : null}
        {errorAction(error)}

        {sel ? primary(
          sel.kind === 'stay' ? keepTierLabel(fromName) : switchButtonLabel(shortDate(renewsAtMs)),
          confirmSwitch,
          { disabled, busy },
        ) : null}
        {sel ? <ConsentHint visible={!consent} /> : null}
        {secondary('Not now', requestClose)}
      </>
    );
  };

  const renderSwitched = () => (
    <>
      <View style={styles.welcomeIcon}>
        <Ionicons name="star" size={34} color={COLORS.GOLD} />
      </View>
      <Text style={[styles.title, styles.center]}>{copy.memberTitle(name)}</Text>
      {line(switchDone?.text, styles.center)}
      {primary('Done', requestClose)}
      {secondary('Your memberships', () => goFromModal('Memberships'))}
    </>
  );

  const renderWelcome = () => {
    const d = m.doc;
    // A join refused for its card (3.5.8): refunded, never started. Shown
    // here, where the fan is, so they never pay again with the same card.
    const refusal = d && d.startRefused && !(m.isMember && (d.status === 'active' || d.status === 'canceling'))
      && d.startRefused.atMs >= joinStartedAtMs - REFUSAL_SKEW_MS
      ? d.startRefused : null;
    if (refusal) {
      // The split the server recorded (the card part refunded, the account
      // credit returned); an older server records none: the joined price.
      // A team join (3.5b) reads W3 / W4, the same lines naming the team.
      const split = refusal.cashCents != null;
      const refundLine = targetKind === 'team'
        ? teamJoinRefundLine({
          reason: refusal.reason, teamName: name, verify,
          priceCents: split ? refusal.cashCents : joinedPriceCents,
          creditCents: split ? (refusal.creditCents || 0) : 0,
        })
        : joinRefundLine({
          reason: refusal.reason, creatorName: name, last4: null, verify,
          priceCents: split ? refusal.cashCents : joinedPriceCents,
          creditCents: split ? (refusal.creditCents || 0) : 0,
        });
      if (refundLine) {
        return (
          <>
            {title(copy.title(name))}
            <SpendingLimitNotice line={refundLine} onVerify={goVerify} style={styles.limitBox} />
            {refusal.reason === 'card_blocked'
              ? primary(USE_ANOTHER_CARD_LABEL, () => { setError(null); loadOffer(); })
              : null}
            {secondary('Done', requestClose)}
          </>
        );
      }
    }

    let sub = 'wait';
    // A live member now means this payment landed: checkout refuses anyone
    // who already was one. An ended doc counts only when it ended after this
    // join (a re-join reuses an older ended doc).
    if (m.isMember && d && (d.status === 'active' || d.status === 'canceling')) sub = d.status;
    else if (d && d.status === 'ended' && d.endedAtMs > joinStartedAtMs) sub = 'ended';

    if (sub === 'ended') {
      return (
        <>
          {title(copy.title(name))}
          {line(TEXT.checkLatest)}
          {primary('Your memberships', () => goFromModal('Memberships'))}
          {secondary('Done', requestClose)}
        </>
      );
    }
    if (sub === 'wait') {
      return (
        <>
          {title(copy.title(name))}
          {finishingUp ? line(TEXT.finishingUp) : <ActivityIndicator style={styles.loading} color={COLORS.GOLD} />}
          {secondary('Done', requestClose)}
        </>
      );
    }

    const delivered = deliveredPerks(d.perks, d.removedByCreator);
    const hasPerks = listedPerks.some((k) => delivered?.[k] === true);
    let memberLine = copy.welcome(name);
    if (d.removedByCreator) memberLine = hasPerks ? copy.welcomeRemoved(name) : TEXT.checkLatest;
    const endDate = sub === 'canceling' ? formatMembershipDate(d.endsAtMs) : '';
    return (
      <>
        <View style={styles.welcomeIcon}>
          <Ionicons name="star" size={34} color={COLORS.GOLD} />
        </View>
        <Text style={[styles.title, styles.center]}>{"You're in!"}</Text>
        {line(memberLine, styles.center)}
        {endDate ? line(`Your membership ends on ${endDate}.`, styles.center) : null}
        {primary('Done', requestClose)}
        {secondary('Your memberships', () => goFromModal('Memberships'))}
      </>
    );
  };

  const renderBody = () => {
    if (!IS_WEB) {
      return (
        <>
          {title(copy.title(name))}
          {line(TEXT.webOnly)}
          {secondary('Not now', requestClose)}
        </>
      );
    }
    if (!authReady) return <ActivityIndicator style={styles.loading} color={COLORS.GOLD} />;
    if (!uid) {
      return (
        <>
          {title(copy.title(name))}
          {line(TEXT.signUp)}
          {primary('Sign up', () => goFromModal('SignUp'))}
          {secondary('Not now', requestClose)}
        </>
      );
    }
    switch (stage) {
      case 'welcome': return renderWelcome();
      case 'pay': return renderPay();
      case 'checkout': return renderCheckout();
      case 'switch': return renderSwitch();
      case 'switched': return renderSwitched();
      case 'state': return renderExisting(offer?.viewer?.state, offer?.viewer);
      case 'processing': return renderExisting('activating');
      case 'unavailable': return renderUnavailable();
      case 'panel': return renderPanel();
      case 'offer_error':
        return (
          <>
            {title(copy.title(name))}
            {line(error?.text)}
            {primary('Try again', () => { setError(null); loadOffer(); })}
            {secondary('Not now', requestClose)}
          </>
        );
      default:
        return <ActivityIndicator style={styles.loading} color={COLORS.GOLD} />;
    }
  };

  const inCheckout = stage === 'checkout' || stage === 'pay';

  return (
    <Modal visible={!!visible} transparent animationType="fade" onRequestClose={requestClose}>
      {/* The backdrop doesn't close over a live checkout or an in-flight
          call; Not now / Done always do. */}
      <Pressable style={styles.backdrop} onPress={inCheckout || busy ? undefined : requestClose}>
        <Pressable style={[styles.card, inCheckout && styles.cardWide]} onPress={(e) => e.stopPropagation()}>
          <ScrollView showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled">
            {renderBody()}
          </ScrollView>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

// ============================================
// STYLES (dark card + gold, matching the StreamWatch modals)
// ============================================
const styles = StyleSheet.create({
  backdrop: {
    flex: 1, backgroundColor: 'rgba(0,0,0,0.8)',
    justifyContent: 'center', alignItems: 'center', padding: 16,
  },
  card: {
    width: '100%', maxWidth: 400, maxHeight: '92%', backgroundColor: COLORS.CARD,
    borderColor: COLORS.CARD_BORDER, borderWidth: 1, borderRadius: 14, padding: 20,
  },
  cardWide: { maxWidth: 460 },
  title: { color: '#fff', fontSize: 17, fontWeight: '800' },
  sub: { color: '#aaa', fontSize: 13, lineHeight: 19, marginTop: 8 },
  center: { textAlign: 'center' },
  loading: { paddingVertical: 28 },
  perks: { marginTop: 6 },
  perkRow: { flexDirection: 'row', alignItems: 'center', marginTop: 6 },
  perkIcon: { marginRight: 8 },
  perkText: { color: COLORS.TEXT, fontSize: 13.5, flex: 1 },
  tierList: { marginTop: 10 },
  tierRow: {
    borderWidth: 1, borderColor: '#2a2a2a', borderRadius: 10, padding: 12, marginTop: 8, backgroundColor: '#151515',
  },
  tierRowOn: { borderColor: COLORS.GOLD },
  tierRowOff: { opacity: 0.55 },
  tierTop: { flexDirection: 'row', alignItems: 'center' },
  radioIcon: { marginRight: 8, width: 18 },
  tierName: { color: '#fff', fontSize: 14.5, fontWeight: '800', flexShrink: 1 },
  tierPrice: { color: COLORS.GOLD, fontSize: 13.5, fontWeight: '800', marginLeft: 'auto', paddingLeft: 8 },
  currentChip: {
    color: '#0b0b0b', backgroundColor: COLORS.GOLD, fontSize: 10.5, fontWeight: '800',
    borderRadius: 999, paddingHorizontal: 7, paddingVertical: 1, marginLeft: 8, overflow: 'hidden',
  },
  tierLimitText: { color: '#f5c542', fontSize: 12, lineHeight: 17, marginTop: 8 },
  tierLine: { color: COLORS.GOLD, fontSize: 13.5, fontWeight: '700', marginTop: 10 },
  pendingLine: { color: '#ccc', fontSize: 12.5, lineHeight: 18, marginTop: 6 },
  footnote: { color: '#999', fontSize: 12, lineHeight: 17, marginTop: 10 },
  limitBox: { marginTop: 12 },
  warnText: { color: '#f0a040', fontSize: 12.5, lineHeight: 18, marginTop: 14 },
  tierAbovePrice: { color: '#fff', fontSize: 14, fontWeight: '800', marginTop: 14 },
  price: { color: COLORS.GOLD, fontSize: 18, fontWeight: '800', marginTop: 14 },
  priceUnderTier: { marginTop: 2 },
  toggleRow: { flexDirection: 'row', alignItems: 'center', marginTop: 14 },
  toggleText: { color: '#ccc', fontSize: 12.5, lineHeight: 18, flex: 1, marginRight: 10 },
  noticeText: { color: '#f0a040', fontSize: 12.5, lineHeight: 18, marginTop: 12 },
  disclosure: { color: '#999', fontSize: 12, lineHeight: 17, marginTop: 14 },
  disclosureAboveForm: { marginTop: 10, marginBottom: 2 },
  termsLine: { color: '#888', fontSize: 12, lineHeight: 17, marginTop: 8 },
  termsLink: { color: COLORS.GOLD, textDecorationLine: 'underline' },
  errorText: { color: COLORS.RED, fontSize: 12.5, lineHeight: 18, marginTop: 12, textAlign: 'center' },
  primaryBtn: {
    backgroundColor: COLORS.GOLD, borderRadius: 10, paddingVertical: 12,
    alignItems: 'center', marginTop: 14,
  },
  btnDisabled: { opacity: 0.5 },
  primaryBtnText: { color: '#0b0b0b', fontSize: 14, fontWeight: '800' },
  outlineBtn: {
    borderColor: COLORS.GOLD, borderWidth: 1, borderRadius: 10, paddingVertical: 11,
    alignItems: 'center', marginTop: 10,
  },
  outlineBtnText: { color: COLORS.GOLD, fontSize: 14, fontWeight: '800' },
  secondaryBtn: { alignItems: 'center', marginTop: 10, paddingVertical: 8 },
  secondaryBtnText: { color: COLORS.DIM, fontSize: 13, fontWeight: '600' },
  checkoutBox: { minHeight: 420, marginTop: 12, borderRadius: 10, overflow: 'hidden', backgroundColor: '#fff' },
  welcomeIcon: { alignItems: 'center', marginBottom: 8, marginTop: 4 },
  // R6: the pay stage's form, the line under it, the email gate.
  elementBox: { minHeight: 120, marginTop: 12 },
  elementLoading: { paddingVertical: 12 },
  payLineBox: { marginTop: 12 },
  payLineText: { color: COLORS.RED, fontSize: 12.5, lineHeight: 18, textAlign: 'center' },
  payLineBtns: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', gap: 8, marginTop: 8 },
  smallOutlineBtn: {
    borderColor: COLORS.GOLD, borderWidth: 1, borderRadius: 8, paddingVertical: 7, paddingHorizontal: 14,
  },
  smallOutlineText: { color: COLORS.GOLD, fontSize: 12.5, fontWeight: '700' },
  smallGoldBtn: { backgroundColor: COLORS.GOLD, borderRadius: 8, paddingVertical: 8, paddingHorizontal: 14 },
  smallGoldText: { color: '#0b0b0b', fontSize: 12.5, fontWeight: '800' },
  gateBox: {
    marginTop: 14, padding: 12, borderRadius: 10, borderWidth: 1, borderColor: '#2a2a2a', backgroundColor: '#151515',
  },
  gateText: { color: COLORS.TEXT, fontSize: 13, lineHeight: 19 },
});
